import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { S as StringService } from './index2.js';
import { D as DomService, K as Key } from './index3.js';
import { T as TranslationService } from './index6.js';
import { r as remCalc } from './index5.js';
import { d as defineCustomElement$2 } from './roche-icon2.js';
import { d as defineCustomElement$1 } from './roche-tooltip2.js';

const rocheSelectCss = ":host{display:block;margin-top:3rem;margin-top:2rem}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}@media (min-width: 90rem){:host{margin-top:2rem}}:host .select-icon{margin:0.1875rem;fill:#ccc;opacity:0}:host .label{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-weight:400;font-size:var(--label--font-size--small-up);line-height:1.3125rem;display:block;color:#544f4f;margin-bottom:1rem}@media (min-width: 48rem){:host .label{font-size:var(--label--font-size--medium-up);line-height:1.3125rem}}:host .label-wrapper{display:flex;position:relative}:host .label--icon{display:table-cell;vertical-align:middle;padding-left:0.3125rem}:host .label--icon--oncology{fill:#ff7d29}:host .label--icon--immunology{fill:#e085fc}:host .label--icon--ophthalmology{fill:#1482fa}:host .label--icon--infectious{fill:#fac9b5}:host .label--icon--cardiovascular{fill:#26d07c}:host .label--icon--neuroscience{fill:#fbdd7a}:host .label--icon--other{fill:#c2bab5}:host .input{position:relative}:host .input::before{content:\"\";position:absolute;left:0;width:1.125rem;height:1.125rem;border:1px solid #ccc}:host .input--single::before{border-radius:50%}:host .input--checked::before{border-color:var(--color-interactive--primary-idle)}:host .input--checked .select-icon{opacity:1;fill:var(--color-interactive--primary-idle)}:host .item{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:var(--body-small--font-size--small-up);line-height:1.625rem;color:#000;display:table;padding:0 0.25rem}@media (min-width: 48rem){:host .item{font-size:var(--body-small--font-size--medium-up);line-height:1.8125rem}}:host .item:not(:first-of-type){margin-top:0.5rem}:host .item .input,:host .item-label{display:table-cell;vertical-align:middle}:host .item--all{display:flex;width:100%;background:#f5f5f2;align-items:center;padding:0.75rem 0.5rem 0.75rem 0.75rem}:host .item--all:not(:first-of-type){margin-top:0.25rem}:host .item-label{padding-left:0.5rem}:host .item-label:hover{cursor:pointer}:host .item:hover{cursor:pointer}:host .item:hover .input::before{border-color:var(--color-interactive--primary-idle)}:host .item:hover .select-icon{fill:var(--color-interactive--primary-idle)}:host .item:focus .input::before{border-color:var(--color-interactive--primary-idle)}:host .element-container{display:inline}:host .element-container--disabled .label{color:var(--color-interactive--disabled)}:host .element-container--disabled .item,:host .element-container--disabled .item-label{color:var(--color-interactive--disabled);cursor:not-allowed}:host .element-container--disabled .input::before{border-color:#000;background-color:#000;opacity:0.05}:host .element-container--error .input::before{border-color:var(--color-status--error)}:host roche-tooltip{margin:0 0 0 0.625rem}:host([dir=rtl]) .item-label{padding-right:0.5rem;padding-left:0}";
const RocheSelectStyle0 = rocheSelectCss;

const RocheSelect = /*@__PURE__*/ proxyCustomElement(class RocheSelect extends HTMLElement {
    get valid() {
        if (this.required && this.allRequired)
            return this.areAllSelected;
        return this.required ? this.isAnythingSelected : true;
    }
    get value() {
        return this.isAnythingSelected
            ? this.allOptions.filter(({ checked }) => checked).map(({ value }) => value).toString()
            : undefined;
    }
    get optionsParsed() {
        if (!this.options) {
            return [];
        }
        const parsed = StringService.stringToObject(this.options);
        return Object.keys(parsed).map((key) => parsed[key]);
    }
    get allOptions() {
        const parsed = this.optionsParsed;
        return Object.keys(parsed).map((key) => (Object.assign(Object.assign({}, parsed[key]), this.optionStates[key])));
    }
    get isAnythingSelected() {
        return this.allOptions.some((option) => option.checked);
    }
    get areAllSelected() {
        return !this.allOptions.some((option) => !option.checked);
    }
    get itemElements() {
        return Array.from(this.element.shadowRoot.querySelectorAll('.item'));
    }
    handleRocheFormControlInit() {
        this.formControlChangeEvent.emit(this);
    }
    updateStates() {
        this.optionStates = this.optionsParsed.map((option) => ({
            checked: !!option.checked,
        }));
    }
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.formControlChangeEvent = createEvent(this, "rocheFormControlChange", 7);
        this.changeEvent = createEvent(this, "rocheSelectChange", 7);
        this.yOffsetTargetElements = [];
        this.uid = undefined;
        this.name = undefined;
        this.label = undefined;
        this.tooltip = undefined;
        this.required = false;
        this.multiSelect = false;
        this.allRequired = false;
        this.options = undefined;
        this.disabled = false;
        this.labelWrapper = false;
        this.selectAll = false;
        this.enableScrollIntoView = false;
        this.dirty = false;
        this.optionStates = [];
        this.translations = undefined;
        this.handleClick = this.handleClick.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
    }
    async componentWillLoad() {
        this.translations = {
            optional: await TranslationService.translate('optional'),
            all: await TranslationService.translate('all'),
        };
    }
    componentDidLoad() {
        this.updateStates();
        DomService.handleTextDirection(this.element);
    }
    render() {
        const hasError = this.dirty && !this.valid;
        const containerClasses = {
            'element-container': true,
            'element-container--error': hasError,
            'element-container--disabled': this.disabled,
        };
        return (h("div", { key: 'f51bfa014bf40f8e671c031bd0890950913cfc9f', class: containerClasses }, this.areAllSelected, !this.labelWrapper
            && h("div", { class: "label-wrapper" }, h("label", { class: "label" }, this.label, !this.required ? ` (${StringService.capitalize(this.translations.optional)})` : ''), this.tooltip
                && h("roche-tooltip", null, this.tooltip)), this.selectAll
            && h("div", { class: { item: true, 'item--all': this.selectAll }, onKeyDown: () => (this.isAnythingSelected ? this.selectAllOptions() : ''), onClick: () => (this.isAnythingSelected ? this.selectAllOptions() : '') }, h("div", { class: { input: true, [`input--${this.multiSelect ? 'multiple' : 'single'}`]: true, 'input--checked': !this.isAnythingSelected } }, h("roche-icon", { class: 'select-icon', icon: this.multiSelect ? 'checkmark' : 'circle', height: `${remCalc(14)}rem` })), h("label", { class: "item-label" }, StringService.capitalize(this.translations.all))), this.allOptions.map(({ label, value, checked, icon, }, index) => (h("div", { key: `roche-select-${index}-${value}`, class: { item: true, 'item--all': this.selectAll }, onClick: this.handleClick(index), onKeyDown: this.handleClick(index), onBlur: this.handleBlur, tabIndex: this.disabled ? undefined : 0 }, h("div", { class: { input: true, [`input--${this.multiSelect ? 'multiple' : 'single'}`]: true, 'input--checked': checked } }, h("roche-icon", { class: 'select-icon', icon: this.multiSelect ? 'checkmark' : 'circle', height: `${remCalc(14)}rem` })), icon
            && h("roche-icon", { class: { 'label--icon': true, [`label--icon--${value !== 'oncology/hematology' ? value : 'oncology'}`]: true }, icon: icon, height: `${remCalc(17)}rem` }), h("label", { class: "item-label" }, label))))));
    }
    selectAllOptions() {
        this.optionStates = this.optionStates.map((option) => (Object.assign(Object.assign({}, option), { checked: false })));
        this.changeEvent.emit({ index: 0, value: this.optionsParsed[0] });
        this.formControlChangeEvent.emit(this);
        this.dirty = true;
    }
    handleClick(selected) {
        return ({ keyCode }) => {
            if (this.disabled) {
                return;
            }
            if (!keyCode || keyCode === Key.SPACE || keyCode === Key.ENTER) {
                this.optionStates = this.optionStates.map((option, index) => (selected === index ? Object.assign(Object.assign({}, option), { checked: !option.checked }) : Object.assign(Object.assign({}, option), { checked: this.multiSelect ? option.checked : false })));
                this.changeEvent.emit({ index: selected, value: this.optionsParsed[selected] });
                this.formControlChangeEvent.emit(this);
                this.dirty = true;
            }
            if (this.enableScrollIntoView) {
                this.scrollIntoView();
            }
        };
    }
    /**
     * TODO:
     * The select shold not have a method which serves
     * the purpose of a specific integration with the pipeline.
     * Instead, this code should be moved to the pipeline.
     * Ideally, it uses the VisibilityManager
     */
    scrollIntoView() {
        var _a, _b, _c;
        const y = (_a = this.element.parentElement) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().y;
        const yOffset = [...this.yOffsetTargetElements]
            .map((target) => target.getBoundingClientRect().y)
            .reduce((accumulator, value) => accumulator + value, 0);
        window.scrollTo({
            top: (_c = (_b = this.element.parentElement) === null || _b === void 0 ? void 0 : _b.parentElement) === null || _c === void 0 ? void 0 : _c.offsetTop,
            left: y - (yOffset || 0),
            behavior: 'smooth',
        });
    }
    handleBlur() {
        this.dirty = this.dirty
            || this.itemElements.every((element) => document.activeElement !== element);
    }
    get element() { return this; }
    static get watchers() { return {
        "options": ["updateStates"]
    }; }
    static get style() { return RocheSelectStyle0; }
}, [1, "roche-select", {
        "uid": [1],
        "name": [1],
        "label": [1],
        "tooltip": [1],
        "required": [4],
        "multiSelect": [4, "multi-select"],
        "allRequired": [4, "all-required"],
        "options": [1],
        "disabled": [4],
        "labelWrapper": [4, "label-wrapper"],
        "selectAll": [4, "select-all"],
        "enableScrollIntoView": [4, "enable-scroll-into-view"],
        "dirty": [32],
        "optionStates": [32],
        "translations": [32]
    }, [[0, "rocheFormControlInit", "handleRocheFormControlInit"]], {
        "options": ["updateStates"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-select", "roche-icon", "roche-tooltip"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-select":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheSelect);
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "roche-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}

export { RocheSelect as R, defineCustomElement as d };

//# sourceMappingURL=roche-select2.js.map